import React, { useState } from 'react';
import { Building2, Users, Shield, ChevronRight, CheckCircle } from 'lucide-react';
import type { TenantAccessDto } from '@/lib/generated-api';
import { useTheme } from '@/lib/theme-context';
import {
  getBackgroundClasses,
  getTextClasses,
  getBorderClasses,
  getAccentClasses,
  getHoverClasses,
} from '@/lib/theme-utils';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface TenantSelectorProps {
  availableTenants: TenantAccessDto[];
  onSelect: (tenantId: string) => Promise<void>;
  userEmail: string;
  isLoading?: boolean;
}

export default function TenantSelector({
  availableTenants,
  onSelect,
  userEmail,
  isLoading = false,
}: TenantSelectorProps) {
  const { theme } = useTheme();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  const handleTenantSelect = async () => {
    if (!selectedTenantId || isSelecting) return;

    try {
      setIsSelecting(true);
      await onSelect(selectedTenantId);
    } catch (error) {
      console.error('TenantSelector: Error selecting tenant:', error);
      alert(`Failed to select tenant: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSelecting(false);
    }
  };

  const getRoleDisplay = (hasExistingAccess: boolean) =>
    hasExistingAccess ? 'Active Member' : 'Pending Access';

  const getRoleIcon = (hasExistingAccess: boolean) =>
    hasExistingAccess ? (
      <CheckCircle className="w-4 h-4 theme-success" />
    ) : (
      <Shield className="w-4 h-4 theme-warning" />
    );

  return (
    <div className="space-y-6" data-testid="tenant-selector-root">
      <div
        className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg p-3`}
        data-testid="tenant-selector-signed-in"
      >
        <p className={`text-sm ${getTextClasses(theme)}`}>
          <span className={`font-medium ${getTextClasses(theme, 'muted')}`}>Signed in as:</span>{' '}
          {userEmail}
        </p>
      </div>

      <div className="space-y-3" data-testid="tenant-selector-list">
        {availableTenants.map((availableTenant) => {
          const isSelected = selectedTenantId === availableTenant.tenantId;
          return (
            <div
              key={availableTenant.tenantId}
              role="button"
              tabIndex={0}
              className={cn(
                'rounded-lg border-2 p-4 cursor-pointer transition-all duration-200',
                getBorderClasses(theme),
                isSelected
                  ? `${getBackgroundClasses(theme, 'card')} shadow-md`
                  : `${getBackgroundClasses(theme, 'default')} ${getHoverClasses(theme)}`
              )}
              style={
                isSelected
                  ? {
                      borderColor: 'var(--theme-selected)',
                      boxShadow: '0 0 0 1px var(--theme-selected)',
                    }
                  : undefined
              }
              data-testid={`tenant-option-${availableTenant.tenantSlug}`}
              onClick={() => setSelectedTenantId(availableTenant.tenantId!)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedTenantId(availableTenant.tenantId!);
                }
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center mb-3">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center mr-4 shrink-0"
                      style={{
                        backgroundColor: 'var(--theme-accent)',
                        color: 'var(--theme-accent-foreground)',
                      }}
                    >
                      <Building2 className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <h3 className={`text-lg font-semibold ${getTextClasses(theme)} truncate`}>
                        {availableTenant.tenantName}
                      </h3>
                      <p className={`text-sm ${getTextClasses(theme, 'muted')} truncate`}>
                        {availableTenant.tenantSlug}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center min-w-0">
                      <Users className={`w-4 h-4 ${getTextClasses(theme, 'muted')} mr-2 shrink-0`} />
                      <span className={getTextClasses(theme, 'muted')}>Domain:</span>
                      <span className={`ml-2 font-medium ${getTextClasses(theme)} truncate`}>
                        {availableTenant.tenantSlug || 'N/A'}
                      </span>
                    </div>

                    <div className="flex items-center">
                      {getRoleIcon(availableTenant.isActive || false)}
                      <span className={`${getTextClasses(theme, 'muted')} ml-2`}>Access:</span>
                      <span className={`ml-2 font-medium ${getTextClasses(theme)}`}>
                        {getRoleDisplay(availableTenant.isActive || false)}
                      </span>
                    </div>

                    <div className="flex items-center">
                      <Shield className={`w-4 h-4 ${getTextClasses(theme, 'muted')} mr-2 shrink-0`} />
                      <span className={getTextClasses(theme, 'muted')}>Enrollment:</span>
                      <span
                        className={cn(
                          'ml-2 font-medium',
                          availableTenant.isActive ? 'theme-success' : 'theme-warning'
                        )}
                      >
                        {availableTenant.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {!availableTenant.isActive && (
                    <div
                      className="mt-4 p-3 rounded-lg border"
                      style={{
                        backgroundColor: 'var(--theme-info-box-bg)',
                        borderColor: 'var(--theme-warning)',
                      }}
                      role="alert"
                    >
                      <p className="text-xs theme-warning flex items-start gap-1">
                        <Shield className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>
                          You will be granted access to this organization upon selection.
                        </span>
                      </p>
                    </div>
                  )}
                </div>

                {isSelected && (
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'var(--theme-selected)' }}
                    aria-hidden
                  >
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: 'var(--theme-selected-foreground)' }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col space-y-4">
        <Button
          data-testid="tenant-selector-continue"
          type="button"
          disabled={!selectedTenantId || isSelecting || isLoading}
          onClick={() => handleTenantSelect()}
          className={cn(
            'w-full h-11 text-base font-medium',
            getAccentClasses(theme, 'primary')
          )}
          style={{
            backgroundColor: 'var(--theme-accent)',
            color: 'var(--theme-accent-foreground)',
          }}
        >
          {isSelecting || isLoading ? (
            <span className="flex items-center justify-center">
              <span
                className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin mr-3"
                style={{ borderColor: 'var(--theme-accent-foreground)' }}
              />
              Connecting to Organization...
            </span>
          ) : (
            <span className="flex items-center justify-center">
              Continue to{' '}
              {selectedTenantId
                ? availableTenants.find((t) => t.tenantId === selectedTenantId)?.tenantName
                : 'Organization'}
              <ChevronRight className="ml-2 w-5 h-5" />
            </span>
          )}
        </Button>

        <p className={`text-center text-xs ${getTextClasses(theme, 'muted')}`}>
          Need access to a different organization? Contact your system administrator.
        </p>
      </div>

      <div
        className={`rounded-lg p-4 border ${getBorderClasses(theme)}`}
        style={{ backgroundColor: 'var(--theme-info-box-bg)' }}
        data-testid="tenant-selector-security-notice"
      >
        <div className="flex gap-3">
          <Shield className="w-5 h-5 shrink-0 theme-info" />
          <div>
            <h4 className={`text-sm font-medium ${getTextClasses(theme)} mb-1`}>
              Secure Multi-Organization Access
            </h4>
            <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>
              Your access is managed through your organization&apos;s identity provider. You can
              switch between organizations at any time from your account settings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
